"use client";

import { useTranslations } from "next-intl";
import NavigationLink from "@/common/navigation-link.common";
import { validatePassword } from "@/utils/utils";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";

export const LoginForm = ({ role, onSubmit, isLoading, error }) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const authParam =
    role === "user" ? "Mobile Number" : role === "officer" ? "Email ID" : "";

  const handleSubmit = () => {
    const payload =
      role === "user"
        ? {
            mobile_number: mobile,
            password,
            recaptchaToken: captchaToken,
          }
        : {
            email: mobile,
            password,
            recaptchaToken: captchaToken,
          };
    onSubmit(payload);
  };

  const [passwordValidations, setPasswordValidations] = useState({
    isLengthValid: false,
    hasNumber: false,
    hasUpperCase: false,
    hasSpecialChar: false,
    confirmPasswordValid: true,
  });

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const isPasswordValid =
    passwordValidations.isLengthValid &&
    passwordValidations.hasNumber &&
    passwordValidations.hasUpperCase &&
    passwordValidations.hasSpecialChar;

  const isFormValid = isPasswordValid && captchaToken;

  return (
    <Card
      sx={{
        gap: "1.5rem",
        maxWidth: "25rem",
        margin: "auto",
        marginTop: "112px",
      }}
      variant="large"
    >
      <Typography variant="h4">{t("Login to HaritWeg portal")}</Typography>
      <Typography color="light-grey" variant="body1">
        {t("FastTrack Regulated activities in ESZ of Nagpur district")}
      </Typography>

      <div className="w-full flex flex-col gap-6">
        {/* Show error message if there is one */}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        <div className="w-full flex flex-col gap-1">
          <Typography variant="body1"> {t(authParam)} </Typography>
          <TextField
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder={t(`Enter username or ${authParam}`)}
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <PersonOutlineRoundedIcon />
                  </InputAdornment>
                ),
              },
              htmlInput: {
                maxLength: role === "user" ? 10 : 50,
              },
            }}
          />
        </div>

        <div className="w-full flex flex-col gap-1">
          <Typography variant="body1">{t("Password")}</Typography>
          <TextField
            value={password}
            onChange={(e) => {
              const value = e.target.value;
              setPassword(value);
              setPasswordValidations(validatePassword(value));
            }}
            placeholder={t("Enter your password")}
            variant="outlined"
            type={showPassword ? "text" : "password"}
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment
                    sx={{
                      cursor: "pointer",
                    }}
                    position="end"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />
          <div className="flex justify-between items-center">
            <NavigationLink
              href={
                role === "officer"
                  ? "/forgot-password-officer"
                  : "/forgot-password"
              }
            >
              {t("Forgot Password")}
            </NavigationLink>
          </div>
        </div>

        {/* reCAPTCHA integration */}
        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            theme="light"
          />
        </div>

        <Button
          variant="contained"
          fullWidth
          endIcon={<LoginRoundedIcon />}
          onClick={handleSubmit}
          disabled={isLoading || !isFormValid}
          sx={{
            "&.Mui-disabled": {
              pointerEvents: "auto",
              cursor: "not-allowed",
              opacity: 0.5,
              ":hover": {
                color: "white",
              },
            },
          }}
        >
          {isLoading
            ? t("LoggingIn", { fallback: "Logging in..." })
            : t("Login")}
        </Button>

        {role === "user" && (
          <Typography
            sx={{
              fontSize: "1rem",
              color: "#4B5563",
            }}
            color="grey"
            variant="body1"
            align="center"
          >
            {t("New User")}{" "}
            <NavigationLink href="/register">
              {t("Register Here")}
            </NavigationLink>
          </Typography>
        )}

        <Divider />
        <Typography
          sx={{
            fontSize: "1rem",
            color: "#4B5563",
          }}
          color="grey"
          variant="body1"
          align="center"
        >
          {t("Need Help Contact our support team")}{" "}
          <NavigationLink>{t("supportforestdeptgovin")}</NavigationLink>
        </Typography>
      </div>
    </Card>
  );
};

export default LoginForm;
